import { useState } from "react";
import { Mail, Phone, Landmark, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { ScrollReveal } from "./Reveal";
import HankoSeal from "./HankoSeal";

const EMAIL = "Sorajapaneseschool48@gmail.com";
const PHONE = "0724546776";

const CopyBtn = ({ value, testid }) => {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      toast.success("Copiat în clipboard.");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Nu am putut copia.");
    }
  };
  return (
    <button
      data-testid={testid}
      onClick={copy}
      aria-label="Copiază"
      className="w-11 h-11 flex items-center justify-center rounded-full border border-sumi/15 text-charcoal/60 hover:text-hanko hover:border-hanko transition-colors duration-300"
    >
      {copied ? <Check size={17} className="text-hanko" /> : <Copy size={17} />}
    </button>
  );
};

export default function Contact() {
  return (
    <section id="contact" data-testid="contact-section" className="relative py-20 sm:py-32 bg-washiaccent/50 border-t border-sumi/10 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-8">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <ScrollReveal>
              <p className="text-xs font-mono tracking-[0.3em] uppercase text-hanko mb-4">Contact & Plată</p>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl text-sumi leading-tight">
                Hai să vorbim. <span className="italic text-hanko">話しましょう。</span>
              </h2>
            </ScrollReveal>

            <div className="mt-10 divide-y divide-sumi/10 border-y border-sumi/10">
              <ScrollReveal>
                <div data-testid="contact-email-row" className="flex items-center gap-4 py-6">
                  <span className="w-11 h-11 rounded-full bg-hanko/10 flex items-center justify-center shrink-0">
                    <Mail size={19} className="text-hanko" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-mono tracking-widest uppercase text-charcoal/50">Email</p>
                    <a href={`mailto:${EMAIL}`} data-testid="contact-email-link" className="text-sumi font-medium hover:text-hanko transition-colors break-all">
                      {EMAIL}
                    </a>
                  </div>
                  <CopyBtn value={EMAIL} testid="contact-email-copy-button" />
                </div>
              </ScrollReveal>
              <ScrollReveal delay={0.05}>
                <div data-testid="contact-phone-row" className="flex items-center gap-4 py-6">
                  <span className="w-11 h-11 rounded-full bg-hanko/10 flex items-center justify-center shrink-0">
                    <Phone size={19} className="text-hanko" />
                  </span>
                  <div className="flex-1 min-w-0">
                    <p className="text-[11px] font-mono tracking-widest uppercase text-charcoal/50">Telefon</p>
                    <a href={`tel:${PHONE}`} data-testid="contact-phone-link" className="text-sumi font-medium hover:text-hanko transition-colors">
                      {PHONE}
                    </a>
                  </div>
                  <CopyBtn value={PHONE} testid="contact-phone-copy-button" />
                </div>
              </ScrollReveal>
              <ScrollReveal delay={0.1}>
                <div data-testid="contact-payment-row" className="flex items-start gap-4 py-6">
                  <span className="w-11 h-11 rounded-full bg-hanko/10 flex items-center justify-center shrink-0">
                    <Landmark size={19} className="text-hanko" />
                  </span>
                  <div>
                    <p className="text-[11px] font-mono tracking-widest uppercase text-charcoal/50">Plată</p>
                    <p className="text-sumi font-medium">Transfer bancar</p>
                    <p className="text-sm text-charcoal/65 mt-1 leading-relaxed">
                      Detaliile contului le primești pe email după confirmarea locului.
                      Plata se face integral sau în două rate egale.
                    </p>
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>

          <div className="lg:col-span-5 flex flex-col items-center text-center">
            <ScrollReveal delay={0.15}>
              <div className="animate-seal-drift flex justify-center">
                <HankoSeal size={150} animate={false} />
              </div>
              <p className="mt-8 font-serif italic text-xl text-charcoal/70 max-w-xs">
                „一期一会 — fiecare întâlnire e unică. Trăiește-o ca atare."
              </p>
              <p className="mt-2 text-xs font-mono tracking-widest uppercase text-charcoal/45">
                Sen no Rikyū · maestrul ceremoniei ceaiului
              </p>
            </ScrollReveal>
          </div>
        </div>
      </div>

      <footer data-testid="site-footer" className="mt-20 sm:mt-28 border-t border-sumi/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-8 py-10 flex flex-col sm:flex-row items-center justify-between gap-6">
          <p className="text-xs text-charcoal/55 text-center sm:text-left">
            SORA · limbă, cultură și stil de viață japonez · pentru toate vârstele
          </p>
          <p className="text-xs text-charcoal/45">© 2026 SORA — Școala de Japoneză, București</p>
          <div className="flex items-center gap-3">
            <img src="/sora-logo.png" alt="SORA" className="h-8 w-auto" />
            <span className="font-mono font-bold tracking-[0.35em] text-xs text-sumi">SORA</span>
          </div>
        </div>
      </footer>
    </section>
  );
}
